import { setNotification } from "features/Notification/notificationSlice";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import authHeader from "services/auth-header";
import API from "services/axios";
import useInbox from "../../InboxTab/InboxContext";
import Spinner from "components/Spinner";

// const EMAIL_IFRAME_BASE_STYLES = `
//   :root {
//     color-scheme: light;
//   }
//   body {
//     font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
//     font-size: 14px;
//     line-height: 1.5;
//     margin: 0;
//     padding: 12px;
//     color: #0f172a;
//     background: #ffffff;
//     overflow-wrap: break-word;
//   }
//   * {
//     box-sizing: border-box;
//     max-width: 100%;
//   }
//   img {
//     height: auto;
//   }
//   table {
//     border-collapse: collapse;
//     table-layout: fixed;
//     width: 100%;
//   }
//   pre, code {
//     white-space: pre-wrap;
//     word-break: break-word;
//   }
// `;

const EMAIL_IFRAME_BASE_STYLES = `
  :root {
    color-scheme: light;
  }
  body {
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    font-size: 14px;
    line-height: 1.5;
    margin: 0;
    padding: 12px;
    color: #0f172a;
    background: #ffffff;
    overflow-wrap: break-word;
  }
  * {
    box-sizing: border-box;
    max-width: 100%;
  }
  img {
    height: auto;
  }
  table {
    border-collapse: collapse;
    table-layout: fixed;
    width: 100%;
  }
  pre, code {
    white-space: pre-wrap;
    word-break: break-word;
  }
`;

export default function EmailBody({ message, refreshFunc }) {
  /*   All States
   ********************************************* */
  const dispatch = useDispatch();
  const {
    getFilteredLabelNames,
    refreshLabels,
    handleBulkDelete,
    handleBulkReadStatus,
    messageId,
  } = useInbox();
  const [showAttachments, setShowAttachments] = useState(true);
  const [loading, setLoading] = useState(false);
  const emailIframeRef = React.useRef(null);

  /*   All Functions
   ********************************************* */

  const getLabelBadgeClass = (labelName) => {
    const value = (labelName || "").toLowerCase();

    if (value === "inbox") return "bg-secondary";
    if (value === "sent") return "bg-sent";
    if (value === "archived_mails") return "bg-primary";
    if (value === "archived") return "bg-primary";
    if (value === "trash") return "bg-danger";
    if (value === "spam") return "bg-danger";

    return "bg-info";
  };

  const looksLikeBase64 = React.useCallback((value) => {
    if (!value || typeof value !== "string") return false;

    const sanitized = value
      .replace(/\s+/g, "")
      .replace(/-/g, "+")
      .replace(/_/g, "/");

    if (!sanitized || /[^A-Za-z0-9+/=]/.test(sanitized)) return false;

    if (sanitized.length % 4 === 1) return false;

    const paddingLength = (4 - (sanitized.length % 4 || 4)) % 4;
    const padded = sanitized + "=".repeat(paddingLength);

    try {
      const decoded = atob(padded);
      const reencoded = btoa(decoded).replace(/=+$/, "");
      const normalizedOriginal = sanitized.replace(/=+$/, "");
      return reencoded === normalizedOriginal;
    } catch (error) {
      return false;
    }
  }, []);

  const isMostlyPrintable = React.useCallback((value) => {
    if (!value || typeof value !== "string") return true;
    let printable = 0;
    for (let index = 0; index < value.length; index += 1) {
      const code = value.charCodeAt(index);
      if (
        (code >= 32 && code <= 126) ||
        code === 9 ||
        code === 10 ||
        code === 13
      ) {
        printable += 1;
      }
    }
    return printable / value.length >= 0.85;
  }, []);

  const decodeBodyData = React.useCallback(
    (data) => {
      if (!data || typeof data !== "string") return "";

      if (!looksLikeBase64(data)) return data;

      try {
        const sanitized = data
          .replace(/\s+/g, "")
          .replace(/-/g, "+")
          .replace(/_/g, "/");

        const paddingLength = (4 - (sanitized.length % 4 || 4)) % 4;
        const padded = sanitized + "=".repeat(paddingLength);
        const decoded = atob(padded);

        let unicodeDecoded = decoded;

        try {
          unicodeDecoded = decodeURIComponent(
            Array.prototype.map
              .call(
                decoded,
                (c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2),
              )
              .join(""),
          );
        } catch (uriError) {
          unicodeDecoded = decoded;
        }

        if (!isMostlyPrintable(unicodeDecoded)) {
          return data;
        }

        return unicodeDecoded;
      } catch (decodeError) {
        return data;
      }
    },
    [isMostlyPrintable, looksLikeBase64],
  );

  const sanitizeEmailBody = React.useCallback((html) => {
    if (!html || typeof html !== "string") return "";

    if (typeof window === "undefined" || typeof DOMParser === "undefined") {
      return html;
    }

    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");
      doc
        .querySelectorAll("script,style,link[rel='stylesheet'],iframe")
        .forEach((el) => el.remove());
      return doc.body.innerHTML || "";
    } catch (error) {
      return html;
    }
  }, []);

  const preparedBody = React.useMemo(() => {
    const rawBodyData = message?.bodyData ?? "";
    const rawBodyFallback = message?.body ?? "";
    const sourceBody = rawBodyData || rawBodyFallback;
    const decodedSource = decodeBodyData(sourceBody);
    const effectiveBody =
      rawBodyData && decodedSource === sourceBody
        ? rawBodyFallback
        : decodedSource;
    const content = effectiveBody ?? "";
    const htmlPattern = /<\/?[a-z][\s\S]*>/i;

    if (!content) {
      return { content: "", isHtml: false };
    }

    if (!htmlPattern.test(content)) {
      return { content, isHtml: false };
    }

    return { content: sanitizeEmailBody(content), isHtml: true };
  }, [decodeBodyData, message, sanitizeEmailBody]);

  const writeEmailIframe = React.useCallback((htmlContent = "") => {
    const iframe = emailIframeRef.current;
    if (!iframe) return;
    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) return;
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8" />
      <style>${EMAIL_IFRAME_BASE_STYLES}</style></head>
      <body>${htmlContent || ""}</body></html>`;
    doc.open();
    doc.write(html);
    doc.close();

    const links = doc.querySelectorAll("a[href]");
    links.forEach((link) => {
      link.setAttribute("target", "_blank");
      link.setAttribute("rel", "noopener noreferrer");
    });
  }, []);

  useEffect(() => {
    if (preparedBody.isHtml) {
      writeEmailIframe(preparedBody.content);
    } else {
      writeEmailIframe("");
    }
  }, [preparedBody, writeEmailIframe]);

  const attachments = React.useMemo(
    () => message?.attachments || [],
    [message],
  );

  const formatBytes = React.useCallback((bytes) => {
    if (!bytes || Number.isNaN(Number(bytes))) return "";
    const sizes = ["B", "KB", "MB", "GB"]; // likely sufficient for email attachments
    const value = Number(bytes);
    if (value === 0) return "0 B";
    const index = Math.min(
      Math.floor(Math.log(value) / Math.log(1024)),
      sizes.length - 1,
    );
    const converted = value / Math.pow(1024, index);
    return `${converted.toFixed(converted >= 10 || index === 0 ? 0 : 1)} ${
      sizes[index]
    }`;
  }, []);

  const getAttachmentUrl = React.useCallback((attachment) => {
    return (
      attachment?.downloadUrl ||
      attachment?.download_url ||
      attachment?.fileUrl ||
      attachment?.url ||
      null
    );
  }, []);

  const handleAttachmentDownload = React.useCallback(
    async (attachment) => {
      if (!attachment) return;

      const directUrl = getAttachmentUrl(attachment);
      if (directUrl) {
        window.open(directUrl, "_blank", "noopener,noreferrer");
        return;
      }

      if (!attachment?.id || !message?.id) {
        dispatch(
          setNotification({
            type: "error",
            message: "Attachment identifiers are missing.",
          }),
        );
        return;
      }

      try {
        const response = await API.get(
          `/emails/attachment/${message.id}/${attachment.id}`,
          {
            responseType: "blob",
            headers: authHeader(),
          },
        );

        let filename = attachment?.filename || "attachment";
        const disposition = response.headers?.["content-disposition"] ?? "";
        const match = disposition.match(
          /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i,
        );
        if (match?.[1]) {
          filename = decodeURIComponent(match[1].replace(/['"]/g, ""));
        }

        const blob = new Blob([response.data], {
          type:
            attachment?.contentType ||
            attachment?.content_type ||
            response.data.type ||
            "application/octet-stream",
        });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        const defaultMessage =
          "Unable to download attachment. Please try again.";
        if (
          error?.response?.data instanceof Blob &&
          error.response.data.type?.includes("application/json")
        ) {
          error.response.data.text().then((text) => {
            const payload = JSON.parse(text || "{}");
            dispatch(
              setNotification({
                type: "error",
                message: payload?.message || defaultMessage,
              }),
            );
          });
        } else {
          dispatch(
            setNotification({
              type: "error",
              message:
                error?.response?.data?.message ||
                error?.message ||
                defaultMessage,
            }),
          );
        }
      }
    },
    [dispatch, getAttachmentUrl, message],
  );

  const handleDeleteMessage = async (messageId) => {
    try {
      if (!messageId) {
        return dispatch(
          setNotification({
            message: "Invalid email selected",
            type: "error",
          }),
        );
      }

      setLoading(true);
      let success = await handleBulkDelete([messageId]);
      if (success) {
        // refreshLabels();
        const result = await refreshFunc(message.messageId);
        // If result is false or null, it means message is empty, so go back to list
        if (result === false || result === null) {
          return; // refreshFunc will handle navigation
        }
      } else {
        dispatch(
          setNotification({
            message: "Failed to delete email",
            type: "error",
          }),
        );
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
    }
  };

  useEffect(() => {
    if (message) {
      if (message.unread) {
        // send mark as read request
        handleBulkReadStatus([message.id], true);
        // refreshLabels();
      }
    }
  }, [message]);

  /*   All UseEffects
   ********************************************* */

  return (
    <div className="bg-white flex-grow-1 bg-info py-2 px-2 custom-border-radius shadow-sm">
      {loading && <Spinner />}
      <div className=" ">
        <div className="py-1">
          <div className="px-2 d-flex justify-content-between align-items-center gap-3 ">
            <div className="mt-0 mb-2 text-mute-2 d-flex align-items-center">
              <span className="fs-4 fw-bold ">
                Subject: {message?.subject || "(No Subject)"}
              </span>
              <span className="ms-3">
                {getFilteredLabelNames(message?.folders).map((labelName) => (
                  <span
                    key={labelName}
                    className={`badge ${getLabelBadgeClass(
                      labelName,
                    )} fs-6 text-white me-1`}
                  >
                    {labelName === "archived_mails" ? "Archived" : labelName}
                  </span>
                ))}
              </span>
            </div>
          </div>
        </div>
      </div>
      {/* <hr /> */}
      <div className="overflow-auto">
        {/* ====== Right Side Messages */}
        <div className="">
          <div className="px-2 ">
            {/* Body */}
            {preparedBody.isHtml ? (
              <div
                style={{
                  overflow: "auto",
                  maxWidth: "100%",
                  wordBreak: "break-word",
                  overflowWrap: "break-word",
                }}
              >
                <iframe
                  title="Email content"
                  ref={emailIframeRef}
                  sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation"
                  style={{
                    border: "1px solid #e2e8f0",
                    borderRadius: 10,
                    width: "100%",
                    minHeight: 400,
                    backgroundColor: "#fff",
                  }}
                />
              </div>
            ) : (
              <pre
                className="email-body text-mute-2"
                style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
              >
                {preparedBody.content}
              </pre>
            )}

            {attachments.length > 0 && <hr />}

            {attachments.length > 0 && (
              <div className="d-flex justify-content-start align-items-center gap-2">
                <hr />
                <button
                  className={`btn btn-sm py-0 px-1 ${
                    showAttachments ? "btn-success" : "btn-outline-secondary"
                  }`}
                  onClick={() => setShowAttachments(!showAttachments)}
                >
                  <i className={`mdi mdi-attachment fs-4`}></i>
                </button>
                <div className="fw-semibold text-mute-2">
                  Attachments ({attachments.length})
                </div>
              </div>
            )}

            {attachments.length > 0 && (
              <div
                className={`mt-3 ${
                  showAttachments ? "opacity-100 h-100" : "opacity-0 h-0"
                } `}
                style={{
                  transition: "all 0.5s ease-in-out",
                }}
              >
                {showAttachments && (
                  <>
                    <div className="fw-semibold text-mute-2 mb-2">
                      Attachments ({attachments.length})
                    </div>
                    <ul className="list-unstyled d-flex flex-column gap-2 mb-0">
                      {attachments.map((attachment, index) => {
                        const sizeLabel = formatBytes(attachment?.size);

                        return (
                          <li
                            key={
                              attachment?.id ||
                              `${attachment?.filename}-${index}`
                            }
                            className="d-flex justify-content-between align-items-center bg-light rounded-2 px-3 py-2"
                          >
                            <div className="d-flex align-items-center text-mute-2">
                              <i
                                className="mdi mdi-paperclip me-2"
                                aria-hidden="true"
                              ></i>
                              <div>
                                <div className="fw-medium">
                                  {attachment?.filename || "Attachment"}
                                </div>
                                {sizeLabel && (
                                  <div className="text-muted small">
                                    {sizeLabel}
                                  </div>
                                )}
                              </div>
                            </div>
                            <button
                              type="button"
                              className="btn btn-outline-primary btn-sm"
                              onClick={() =>
                                handleAttachmentDownload(attachment)
                              }
                            >
                              Download
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
